import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  TextField,
  Stack,
  InputAdornment,
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  Fade,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import AddIngredients from "../inventory/AddIngredients";
import { useNotification } from "../../Context/notificationContext";
import { GET_INGREDIENTS } from "./ChooseGrainsForm";
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import ScaleIcon from '@mui/icons-material/Scale';

export default function PourCupForm({ task, onSubmit }) {
  const [cupName, setCupName] = useState(task.item_name || "");
  const [quantity, setQuantity] = useState(task.quantity || "");
  const [itemError, setItemError] = useState("");
  const [error, setError] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { showNotification } = useNotification();
  const minQuantity = 1;
  const maxQuantity = 200;

  const {
    data,
    loading: getIngredientsLoading,
    error: getIngredientsError,
    refetch: refetchIngredients,
  } = useQuery(GET_INGREDIENTS, {
    variables: { ingredientType: "ALL" },
    fetchPolicy: "cache-and-network",
    onError: (error) => showNotification("error", error.message),
  });
  console.warn("getIngredientsError", getIngredientsError)
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validateForm()) {
      try {
        setFormSubmitting(true);
        task.args = `${cupName} - ${quantity}g`;
        task.quantity = quantity;
        task.item_name = cupName;
        await onSubmit(task);
        showNotification("success", "Cup poured successfully!");
      } catch (err) {
        showNotification("error", "Failed to submit form. Please try again.");
      } finally {
        setFormSubmitting(false);
      }
    }
  };

  const validateForm = () => {
    if (!cupName) {
      setItemError("Please select a cup item");
      setError("");
      return false;
    }

    if (!quantity) {
      setError("Please enter a quantity");
      setItemError("");
      return false;
    }

    const quantityNum = parseFloat(quantity);
    if (isNaN(quantityNum) || quantityNum < minQuantity || quantityNum > maxQuantity) {
      setError(`Quantity must be between ${minQuantity} and ${maxQuantity} grams`);
      setItemError("");
      return false;
    }

    setError("");
    setItemError("");
    return true;
  };

  const [addIngredientActive, setAddIngredientActive] = useState(false);
  const [item_name, setItem_name] = useState("");
  const [ingredientType, setIngredientType] = useState(null);
  const [ingredientImage, setIngredientImage] = useState(null);
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    if (data?.getIngredients) {
      setIngredients(data.getIngredients);
    }
  }, [data]);

  return (
    <Fade in={true} timeout={300}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        {addIngredientActive ? (
          <AddIngredients
            newIngredientName={item_name}
            setNewIngredientName={setItem_name}
            ingredientType={ingredientType}
            setIngredientType={setIngredientType}
            ingredientImage={ingredientImage}
            setIngredientImage={setIngredientImage}
            ingredients={ingredients}
            setIngredients={setIngredients}
            setAddIngredientActive={setAddIngredientActive}
            refetchIngredients={refetchIngredients}
          />
        ) : (
          <>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <LocalCafeIcon color="primary" /> Pour Cup
            </Typography>

            {(error || itemError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error || itemError}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={ingredients}
                    getOptionLabel={(option) => option.ingredient_name || ""}
                    loading={getIngredientsLoading}
                    filterOptions={(options, { inputValue }) =>
                      options.filter((option) =>
                        option.ingredient_name.toLowerCase().includes(inputValue.toLowerCase())
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Cup Item"
                        error={!!itemError}
                        helperText={itemError}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <LocalCafeIcon color="action" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {getIngredientsLoading ? <CircularProgress size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', overflow: 'hidden' }}>
                          <img
                            src={option.image_url || "/placeholder-16x9.png"}
                            alt={option.ingredient_name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={(e) => { e.target.src = "/placeholder-16x9.png"; }}
                          />
                        </Box>
                        <Typography>{option.ingredient_name}</Typography>
                      </Box>
                    )}
                    noOptionsText={
                      getIngredientsLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                          <CircularProgress size={20} />
                          <Typography>Loading ingredients...</Typography>
                        </Box>
                      ) : (
                        <Box sx={{ p: 1 }}>
                          <Typography sx={{ mb: 1 }}>Ingredient not found in the list</Typography>
                          <Button
                            variant="contained"
                            onClick={() => {
                              setItem_name(cupName);
                              setAddIngredientActive(true);
                            }}
                            fullWidth
                          >
                            Add New Ingredient
                          </Button>
                        </Box>
                      )
                    }
                    onInputChange={(_, newValue) => setCupName(newValue)}
                    value={ingredients.find(item => item.ingredient_name === cupName) || null}
                    disabled={getIngredientsLoading}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <TextField
                    label="Enter Quantity"
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    error={!!error}
                    helperText={error || `Enter between ${minQuantity}-${maxQuantity} grams`}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ScaleIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">grams</InputAdornment>
                      ),
                      inputProps: { min: minQuantity, max: maxQuantity }
                    }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={formSubmitting || getIngredientsLoading}
                  sx={{
                    py: 1.5,
                    fontWeight: 'bold',
                    mt: 1,
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-2px)' }
                  }}
                >
                  {formSubmitting ? (
                    <>
                      <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                      Processing...
                    </>
                  ) : (
                    'Pour Cup'
                  )}
                </Button>
              </Stack>
            </form>
          </>
        )}
      </Paper>
    </Fade>
  );
}
